/**
 * Secure User Profile Resolution Service
 *
 * This service provides secure, cached profile name resolution via Cloud Function.
 * It replaces direct Firestore reads of /users/{uid}/profile/data for cross-user lookups.
 *
 * SECURITY (Audit Jan 2026):
 * All cross-user profile resolution MUST go through this service.
 * The Cloud Function verifies that the caller shares at least one team with the target.
 *
 * Features:
 * - Batch resolution (up to 50 UIDs per call)
 * - In-memory caching with configurable TTL
 * - Automatic deduplication
 * - Graceful fallback for denied/missing profiles
 *
 * Usage:
 * ```typescript
 * const resolver = getProfileResolver();
 * const profiles = await resolver.resolve(['uid1', 'uid2']);
 * // profiles = { uid1: 'John Doe', uid2: null }
 * ```
 */

import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '@/lib/firebase';
import { logger } from '@/lib/logger';

// =============================================================================
// Types
// =============================================================================

interface UserProfileResult {
  displayName: string | null;
}

interface ResolveUserProfilesResponse {
  profiles: Record<string, UserProfileResult>;
}

interface CacheEntry {
  displayName: string | null;
  expiresAt: number;
}

// =============================================================================
// Configuration
// =============================================================================

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_BATCH_SIZE = 50;

// =============================================================================
// Cache
// =============================================================================

const profileCache = new Map<string, CacheEntry>();

/**
 * Get a cached profile if it exists and hasn't expired.
 */
function getCached(uid: string): string | null | undefined {
  const entry = profileCache.get(uid);
  if (!entry) return undefined;

  if (Date.now() > entry.expiresAt) {
    profileCache.delete(uid);
    return undefined;
  }

  return entry.displayName;
}

/**
 * Set a cached profile with TTL.
 */
function setCache(uid: string, displayName: string | null): void {
  profileCache.set(uid, {
    displayName,
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
}

/**
 * Clear the entire cache.
 * Useful on logout or team switch.
 */
export function clearProfileCache(): void {
  profileCache.clear();
}

/**
 * Invalidate a specific profile from cache.
 */
export function invalidateProfile(uid: string): void {
  profileCache.delete(uid);
}

// =============================================================================
// Cloud Function Reference
// =============================================================================

const resolveUserProfilesFn = httpsCallable<{ targetUids: string[] }, ResolveUserProfilesResponse>(
  functions,
  'resolveUserProfiles'
);

// =============================================================================
// Resolver Class
// =============================================================================

class ProfileResolver {
  private pendingRequests = new Map<string, Promise<string | null>>();

  /**
   * Resolve a single user's display name.
   * Returns null if not authorized or profile not found.
   */
  async resolveOne(uid: string): Promise<string | null> {
    if (!uid) return null;

    // Check cache first
    const cached = getCached(uid);
    if (cached !== undefined) {
      return cached;
    }

    // Check if there's already a pending request for this UID
    const pending = this.pendingRequests.get(uid);
    if (pending) {
      return pending;
    }

    // Create a new request
    const promise = this.fetchProfiles([uid]).then((result) => result[uid] ?? null);
    this.pendingRequests.set(uid, promise);

    try {
      return await promise;
    } finally {
      this.pendingRequests.delete(uid);
    }
  }

  /**
   * Resolve multiple user display names in a single batch.
   * Returns a map of uid -> displayName (null if not authorized or not found).
   */
  async resolve(uids: string[]): Promise<Record<string, string | null>> {
    if (!uids || uids.length === 0) {
      return {};
    }

    // Deduplicate and filter empty strings
    const uniqueUids = [...new Set(uids.filter((uid) => uid && uid.length > 0))];

    // Separate cached and uncached UIDs
    const result: Record<string, string | null> = {};
    const uncachedUids: string[] = [];

    for (const uid of uniqueUids) {
      const cached = getCached(uid);
      if (cached !== undefined) {
        result[uid] = cached;
      } else {
        uncachedUids.push(uid);
      }
    }

    // If all are cached, return immediately
    if (uncachedUids.length === 0) {
      return result;
    }

    // Fetch uncached profiles in batches
    const batches: string[][] = [];
    for (let i = 0; i < uncachedUids.length; i += MAX_BATCH_SIZE) {
      batches.push(uncachedUids.slice(i, i + MAX_BATCH_SIZE));
    }

    const batchResults = await Promise.all(batches.map((batch) => this.fetchProfiles(batch)));

    // Merge batch results
    for (const batchResult of batchResults) {
      Object.assign(result, batchResult);
    }

    return result;
  }

  /**
   * Fetch profiles from the Cloud Function and cache results.
   * Gates on auth state to avoid "Unauthenticated" errors during startup.
   */
  private async fetchProfiles(uids: string[]): Promise<Record<string, string | null>> {
    // Gate on auth state - if not authenticated, return null for all UIDs
    // This is transient during app startup, not an error
    if (!auth.currentUser) {
      // Don't log as error - this is expected during startup
      const result: Record<string, string | null> = {};
      for (const uid of uids) {
        result[uid] = null;
      }
      return result;
    }

    try {
      const response = await resolveUserProfilesFn({ targetUids: uids });
      const profiles = response.data.profiles;

      const result: Record<string, string | null> = {};

      for (const uid of uids) {
        const profile = profiles[uid];
        const displayName = profile?.displayName ?? null;

        // Cache the result
        setCache(uid, displayName);
        result[uid] = displayName;
      }

      return result;
    } catch (error) {
      // Check if this is an unauthenticated error - treat as transient, not error
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isUnauthenticated = errorMessage.toLowerCase().includes('unauthenticated');

      if (isUnauthenticated) {
        // Transient auth state - don't log as error, don't cache
        // Will retry on next request when auth is ready
      } else {
        logger.error('[ProfileResolver] Failed to resolve profiles:', error);
      }

      // On error, return null for all requested UIDs but don't cache
      const result: Record<string, string | null> = {};
      for (const uid of uids) {
        result[uid] = null;
      }
      return result;
    }
  }
}

// =============================================================================
// Singleton Instance
// =============================================================================

let resolverInstance: ProfileResolver | null = null;

/**
 * Get the singleton ProfileResolver instance.
 */
export function getProfileResolver(): ProfileResolver {
  if (!resolverInstance) {
    resolverInstance = new ProfileResolver();
  }
  return resolverInstance;
}

// =============================================================================
// Convenience Functions
// =============================================================================

/**
 * Resolve a single user's display name.
 * Convenience wrapper around getProfileResolver().resolveOne().
 */
export async function resolveUserProfile(uid: string): Promise<string | null> {
  return getProfileResolver().resolveOne(uid);
}

/**
 * Resolve multiple user display names.
 * Convenience wrapper around getProfileResolver().resolve().
 */
export async function resolveUserProfiles(uids: string[]): Promise<Record<string, string | null>> {
  return getProfileResolver().resolve(uids);
}
